"use client"

import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import Navbar from "../../../../components/navbar"
import Footer from "../../../../components/footer"
import TimelineFilter from "../components/TimelineFilter"
import styles from "../css/consultationDetails.module.css"
import ChronicCondition from "../components/chronic-condition"
import FamilyHistory from "../components/family-history"
import Allergies from "../components/allergies"
import Obstetrics from "../components/obstetrics"
import Consult from "../components/consult"
import CreateScript from "../components/create-script"
import { updateAppointmentStatus, createConsultation } from "../../../../api/clinic/consultationApi.js"
import { useAuth } from "../../../../context/AuthContext"
import { useConsultation } from "../../../../context/ConsultationContext"

export default function ConsultationDetails() {
  const [activeStage, setActiveStage] = useState("Chronic Condition")
  const [completedStages, setCompletedStages] = useState([])
  const [visitedStages, setVisitedStages] = useState([])
  const [showCreateScript, setShowCreateScript] = useState(false)
  const [scriptCompleted, setScriptCompleted] = useState(false)
  const [consultationId, setConsultationId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false)
  const [pendingStage, setPendingStage] = useState(null)
  
  // Store form data for each stage
  const [stageData, setStageData] = useState({
    "Chronic Condition": null,
    "Family History": null,
    "Allergies": null,
    "Obstetrics": null,
    "Consult": null,
  })
  
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { consultationData, isInConsultation, currentStep, goToStep } = useConsultation()

  // Extract appointment and patient data from context (preferred) or navigation state (fallback)
  const appointmentData = isInConsultation ? consultationData : (location.state || {})
  const {
    appointmentId,
    patientId,
    patient = {},
    serviceId,
    serviceName,
    dependentCode,
    appointmentDate,
    appointmentTime,
  } = appointmentData

  const patientName = `${patient.firstname || ""} ${patient.surname || ""}`.trim()
  const patientIdNumber = patient.idnumber || ""
  const patientSex = (patient && (patient.sex ?? patient.gender ?? patient.Gender ?? patient.SEX ?? patient.sexname ?? patient.gendername ?? patient.GENDER)) || undefined

  // Validate patient data on mount
  useEffect(() => {
    if (!patientId) {
      setError("No patient data found. Please select a consultation from the list.")
      setLoading(false)
      return
    }
    setLoading(false)
  }, [patientId])

  const handleStageChange = (newStage) => {
    // Check if there are unsaved changes
    if (hasUnsavedChanges) {
      setPendingStage(newStage)
      setShowUnsavedWarning(true)
      return
    }
    
    // Special logic: when jumping to any stage, mark all previous stages as completed
    const stages = ["Chronic Condition", "Family History", "Allergies", "Obstetrics", "Consult"]
    const currentIndex = stages.indexOf(activeStage)
    const newIndex = stages.indexOf(newStage)
    
    // If jumping forward (not to the next immediate stage), mark all intermediate stages as completed
    if (newIndex > currentIndex + 1) {
      const stagesToMarkAsCompleted = stages.slice(currentIndex + 1, newIndex)
      
      // Filter out Obstetrics if patient is male
      const normalizedSex = String(patientSex ?? "").trim().toLowerCase()
      const isMale = normalizedSex === "male"
      const filteredStages = isMale ? stagesToMarkAsCompleted.filter(stage => stage !== "Obstetrics") : stagesToMarkAsCompleted
      
      // Mark all intermediate stages as completed and visited
      const newCompletedStages = [...completedStages]
      const newVisitedStages = [...visitedStages]
      
      filteredStages.forEach(stage => {
        if (!newCompletedStages.includes(stage)) {
          newCompletedStages.push(stage)
        }
        if (!newVisitedStages.includes(stage)) {
          newVisitedStages.push(stage)
        }
      })
      
      setCompletedStages(newCompletedStages)
      setVisitedStages(newVisitedStages)
    }
    
    performStageChange(newStage)
  }
  
  const performStageChange = (newStage) => {
    // Mark the current stage as visited before changing
    if (!visitedStages.includes(activeStage)) {
      setVisitedStages([...visitedStages, activeStage])
    }
    
    setActiveStage(newStage)
    setShowCreateScript(false)
    setHasUnsavedChanges(false)
    setShowUnsavedWarning(false)
    setPendingStage(null)
  }
  
  const handleProceedWithoutSaving = () => {
    if (pendingStage) {
      performStageChange(pendingStage)
    }
  }
  
  const handleCancelNavigation = () => {
    setShowUnsavedWarning(false)
    setPendingStage(null)
  }

  const handleSaveStage = async (data) => {
    // Update stage data (consultation creation is handled by child components)
    setStageData(prev => ({
      ...prev,
      [activeStage]: data
    }))
    
    // Mark as saved
    setHasUnsavedChanges(false)
    
    // Mark stage as completed
    if (!completedStages.includes(activeStage)) {
      setCompletedStages([...completedStages, activeStage])
    }
    if (appointmentId) {
      try { await updateAppointmentStatus(appointmentId, "Completed") } catch (e) {}
    }
  }
  
  const handleDataChange = () => {
    setHasUnsavedChanges(true)
  }
  
  // Helper function for children to ensure consultation exists before saving
  const ensureConsultationExists = async () => {
    if (consultationId) {
      return consultationId
    }
    
    try {
      const payload = {
        patientid: patientId,
        serviceid: serviceId,
        consultationdate: appointmentDate || new Date().toISOString().split('T')[0],
        createdby: user?.userid ? String(user.userid) : undefined,
      }
      const resp = await createConsultation(payload)
      const newId = resp?.data?.consultationid || resp?.consultationid
      setConsultationId(newId)
      return newId
    } catch (e) { throw e }
  }

  const handleCreateScript = async () => {
    console.log("[v0] Create Script button clicked in consultation details")
    try {
      const id = await ensureConsultationExists()
      if (id) setConsultationId(id)

      // Show the regular create script flow for all roles (including PCDT)
      setShowCreateScript(true)
    } catch (e) {
      console.error('Failed to ensure consultation exists before creating script:', e)
    }
  }

  const handleScriptComplete = async () => {
    console.log("[v0] Script completed")
    const isInjectionService = (serviceName || "").toLowerCase().includes("injection")
    setScriptCompleted(isInjectionService)
    setShowCreateScript(false)
    if (appointmentId) {
      try { await updateAppointmentStatus(appointmentId, "Completed") } catch (e) {}
    }
  }

  const renderStageContent = () => {
    if (error) {
      return (
        <div className={styles.errorContainer}>
          <p>{error}</p>
          <button onClick={() => navigate('/customer/clinic/consultations')} className={styles.backButton}>
            Back to Consultations
          </button>
        </div>
      )
    }

    if (showCreateScript) {
      return (
        <CreateScript 
          patientName={`${patientName}(${patientIdNumber})`}
          patientId={patientId}
          consultationId={consultationId}
          appointmentId={appointmentId}
          profileNumber={patient.profilenumber}
          onComplete={handleScriptComplete} 
        />
      )
    }

    const commonProps = {
      consultationId,
      patientData: patient,
      patientName,
      patientIdNumber,
      appointmentId,
      serviceName,
      initialData: stageData[activeStage],
      onDataChange: handleDataChange,
      ensureConsultationExists,
    }

    switch (activeStage) {
      case "Chronic Condition":
        return <ChronicCondition {...commonProps} onSave={handleSaveStage} />
      case "Family History":
        return <FamilyHistory {...commonProps} onSave={handleSaveStage} />
      case "Allergies":
        return <Allergies {...commonProps} onSave={handleSaveStage} />
      case "Obstetrics":
        return <Obstetrics {...commonProps} onSave={handleSaveStage} />
      case "Consult":
        return (
          <Consult
            {...commonProps}
            onSave={handleSaveStage}
            onCreateScript={handleCreateScript}
            showInjectionSection={scriptCompleted}
          />
        )
      default:
        return <ChronicCondition {...commonProps} onSave={handleSaveStage} />
    }
  }

  return (
    <div className={styles.consultationContainer}>
      <Navbar />

      <main className={styles.mainContent}>
        <div className={styles.container}>
          {!showCreateScript && (
            <TimelineFilter
              activeStage={activeStage}
              onStageChange={handleStageChange}
              completedStages={completedStages}
              patientSex={patientSex}
              stageData={stageData}
              visitedStages={visitedStages}
            />
          )}

          {/* Unsaved Changes Warning */}
          {showUnsavedWarning && (
            <div className={styles.modalOverlay}>
              <div className={styles.modalContent}>
                <h3 className={styles.modalTitle}>⚠️ Unsaved Changes</h3>
                <p>You have unsaved changes. Do you want to proceed without saving?</p>
                <div className={styles.modalActions}>
                  <button onClick={handleCancelNavigation} className={styles.stayButton}>
                    Stay & Save
                  </button>
                  <button onClick={handleProceedWithoutSaving} className={styles.discardButton}>
                    Discard Changes
                  </button>
                </div>
              </div>
            </div>
          )}

        {/* Unsaved Changes Indicator */}
        {hasUnsavedChanges && (
          <div className={styles.unsavedBanner}>
            <span className={styles.unsavedIcon}>⚠️</span>
            <span className={styles.unsavedText}>You have unsaved changes. Click "Save" to save your data.</span>
          </div>
        )}

        <div className={styles.contentWrapper}>{renderStageContent()}</div>
      </div>
    </main>

    <Footer />
  </div>
  )
}
